<script lang="ts" setup>
import { computed, ref } from 'vue';

import { TagReference } from '@/api/productTagReferences';
import WarningIcon from '@/components/base/assets/WarningIcon.vue';
import Modal from '@/components/base/layout/Modal.vue';
import Header4 from '@/components/base/typography/Header4.vue';
import Header5 from '@/components/base/typography/Header5.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';

const prop65Tags = ['acrylamide', 'arsenic', 'cadmium', 'lead'];

const props = defineProps<{ productTags: TagReference[] }>();

const showModal = ref(false);

const matchingProp65Tags = computed(() => {
  if (props.productTags) {
    const tagNames = props.productTags.map((tag) => tag.name);
    return prop65Tags.filter((prop) => tagNames.includes(prop));
  }
  return [];
});

const includesProp65Tags = computed(() => matchingProp65Tags.value.length > 0);

const prop65WarningMessage = computed(() => {
  const tags = matchingProp65Tags.value;
  if (tags.length > 0) {
    const verb =
      tags.length > 1 ? `${tags.slice(0, -1).join(', ')} and ${tags.slice(-1)}` : tags[0];
    return `WARNING: Consuming this product can expose you to chemicals including ${verb}, which are known to the State of California to cause cancer and birth defects or other reproductive harm.`;
  }
  return null;
});

const toggleModal = () => {
  showModal.value = !showModal.value;
};
</script>

<template>
  <div v-if="includesProp65Tags" class="flex items-center" data-test="prop65-warning">
    <WarningIcon aria-hidden="true" class="mr-2" :size="16" />
    <SmallBodyText>
      Click here to
      <UnstyledButton data-test="prop65-warning-link" @click="toggleModal">
        <SmallBodyText underline>read</SmallBodyText>
      </UnstyledButton>
      related CA Prop 65 Warning
    </SmallBodyText>
    <Modal
      aria-labelledby="prop-65-warning-title"
      :hasControls="false"
      :isOpen="showModal"
      width="md:max-w-sm"
      @handle-close="toggleModal"
    >
      <template #header>
        <Header5 class="text-center" headerTag="p" id="prop-65-warning-title">
          California Prop 65 Warning
        </Header5>
        <header class="mt-1 text-center">
          <Header4 class="text-center">
            Warning
            <WarningIcon aria-hidden="true" class="align-middle" :size="16" />
          </Header4>
        </header>
      </template>
      <template #body>
        <div class="mt-6">
          <SmallBodyText>{{ prop65WarningMessage }}</SmallBodyText>
          <SmallBodyText class="mt-2">
            For more information go to
            <a href="https://www.P65Warnings.ca.gov/food" target="_blank">
              https://www.P65Warnings.ca.gov/food
            </a>
          </SmallBodyText>
        </div>
      </template>
    </Modal>
  </div>
</template>
